import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { orderService, settingService } from '../../services/api';
import styles from './Checkout.module.css';

const Checkout = () => {
  const navigate = useNavigate();
  const { items, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const { addNotification } = useNotifications();

  const [formData, setFormData] = useState({
    shipping_address: user?.profile?.address || '',
    delivery_phone: user?.profile?.phone || user?.phone || '',
    delivery_city: user?.profile?.city || '',
    delivery_region: user?.profile?.region || '',
    payment_method: 'cash_on_delivery',
    notes: '',
    promotion_code: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [promotion, setPromotion] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [validatingPromotion, setValidatingPromotion] = useState(false);
  const [promotionError, setPromotionError] = useState(null);
  const [settings, setSettings] = useState({
    delivery_fee: 500,
    tax_rate: 0.18,
    tax_enabled: true,
    free_delivery_threshold: 50000,
  });
  const [loadingSettings, setLoadingSettings] = useState(true);

  // Charger les settings au montage du composant
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await settingService.getPublicSettings();
        if (response.success && response.data) {
          // Normaliser le taux de TVA : si > 1, diviser par 100 (ex: 10 -> 0.10)
          let taxRate = response.data.tax_rate || 0.18;
          if (taxRate > 1) {
            taxRate = taxRate / 100;
          }

          setSettings({
            delivery_fee: response.data.delivery_fee || 500,
            tax_rate: taxRate,
            tax_enabled: response.data.tax_enabled !== false,
            free_delivery_threshold: response.data.free_delivery_threshold || 50000,
          });
        }
      } catch (error) {
        console.error('Erreur lors du chargement des paramètres:', error);
        // Garder les valeurs par défaut en cas d'erreur
      } finally {
        setLoadingSettings(false);
      }
    };

    loadSettings();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleValidatePromotion = async () => {
    if (!formData.promotion_code.trim()) {
      setPromotionError('Veuillez entrer un code promo');
      return;
    }

    setValidatingPromotion(true);
    setPromotionError(null);

    try {
      const itemsForValidation = items.map(item => ({
        product_id: item.product_id || item.product?.id,
        quantity: item.quantity
      }));

      // Utiliser calculatedSubtotal au lieu de subtotal du contexte
      const response = await orderService.validatePromotionCode(
        formData.promotion_code,
        calculatedSubtotal,
        itemsForValidation,
        null, // shippingCost - ignoré, backend utilise la DB
        null  // taxRate - ignoré, backend utilise la DB
      );

      if (response.success && response.data) {
        setPromotion(response.data.promotion);
        setDiscountAmount(response.data.discount_amount);
        addNotification('Code promo appliqué avec succès !', 'success');
      } else {
        setPromotion(null);
        setDiscountAmount(0);
        setPromotionError(response.message || 'Code promo invalide');
      }
    } catch (error) {
      setPromotion(null);
      setDiscountAmount(0);
      setPromotionError(error.response?.data?.message || 'Erreur lors de la validation du code promo');
    } finally {
      setValidatingPromotion(false);
    }
  };

  const handleRemovePromotion = () => {
    setPromotion(null);
    setDiscountAmount(0);
    setFormData(prev => ({ ...prev, promotion_code: '' }));
    setPromotionError(null);
    addNotification('Code promo retiré', 'info');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const orderData = {
        shipping_address: formData.shipping_address,
        delivery_phone: formData.delivery_phone,
        delivery_city: formData.delivery_city || 'Non spécifié',
        delivery_region: formData.delivery_region || 'Non spécifié',
        payment_method: formData.payment_method,
        notes: formData.notes,
        items: items.map(item => ({
          product_id: item.product_id || item.product?.id,
          quantity: item.quantity
        })),
        shipping_cost: promotion?.type === 'free_delivery' ? 0 : settings.delivery_fee,
        tax_rate: settings.tax_enabled ? settings.tax_rate : 0,
        promotion_code: promotion ? formData.promotion_code : null
      };

      const response = await orderService.createOrder(orderData);

      if (response.success) {
        // Vider le panier après commande réussie
        await clearCart();

        // Notification de succès
        addNotification(
          `Commande validée avec succès ! Numéro de commande: #${response.data.order.id}`,
          'success',
          5000
        );

        // Rediriger vers l'accueil après un court délai
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        const errorMsg = response.message || 'Erreur lors de la création de la commande';
        setError(errorMsg);
        addNotification(errorMsg, 'error');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erreur lors de la création de la commande';
      setError(errorMsg);
      addNotification(errorMsg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculer le subtotal réel à partir des items du panier
  const calculatedSubtotal = useMemo(() => {
    return items.reduce((sum, item) => {
      const price = parseFloat(
        item.promotional_price ||
        item.price ||
        item.unit_price ||
        (item.product?.promotional_price) ||
        (item.product?.sale_price) ||
        (item.product?.price) ||
        0
      );
      return sum + (price * (item.quantity || 0));
    }, 0);
  }, [items]);

  const shippingCost = promotion?.type === 'free_delivery' ? 0 :
    (calculatedSubtotal >= settings.free_delivery_threshold ? 0 : settings.delivery_fee);
  const taxAmount = settings.tax_enabled ? calculatedSubtotal * settings.tax_rate : 0;
  const total = calculatedSubtotal + shippingCost + taxAmount - discountAmount;

  if (items.length === 0) {
    return (
      <div className={styles.checkout}>
        <div className={styles.emptyCart}>
          <h2>Votre panier est vide</h2>
          <p>Ajoutez des produits à votre panier avant de passer commande.</p>
          <button onClick={() => navigate('/')} className={styles.continueShopping}>
            Continuer mes achats
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.checkout}>
      <div className={styles.checkoutContainer}>
        <h1>Finaliser ma commande</h1>

        <div className={styles.checkoutContent}>
          <div className={styles.orderForm}>
            <form onSubmit={handleSubmit}>
              <div className={styles.formSection}>
                <h2>Code promo</h2>

                {!promotion ? (
                  <div className={styles.promotionSection}>
                    <div className={styles.promotionInput}>
                      <input
                        type="text"
                        name="promotion_code"
                        placeholder="Entrez votre code promo"
                        value={formData.promotion_code}
                        onChange={(e) => {
                          if (!validatingPromotion) {
                            handleInputChange(e);
                            setPromotionError(null);
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (!validatingPromotion && formData.promotion_code.trim()) {
                              handleValidatePromotion();
                            }
                          }
                        }}
                        disabled={validatingPromotion}
                        readOnly={false}
                        autoComplete="off"
                        tabIndex={validatingPromotion ? -1 : 0}
                        style={{
                          backgroundColor: validatingPromotion ? '#f5f5f5' : '#ffffff',
                          color: validatingPromotion ? '#999' : '#333',
                          cursor: validatingPromotion ? 'not-allowed' : 'text',
                          opacity: validatingPromotion ? 0.6 : 1
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleValidatePromotion}
                        disabled={validatingPromotion || !formData.promotion_code.trim()}
                        className={styles.promotionButton}
                      >
                        {validatingPromotion ? 'Validation...' : 'Appliquer'}
                      </button>
                    </div>
                    {promotionError && (
                      <div className={styles.promotionError}>{promotionError}</div>
                    )}
                  </div>
                ) : (
                  <div className={styles.promotionApplied}>
                    <div className={styles.promotionInfo}>
                      <span className={styles.promotionCode}>✅ {promotion.code}</span>
                      <span className={styles.promotionName}>{promotion.name}</span>
                      {promotion.description && (
                        <span className={styles.promotionDescription}>{promotion.description}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleRemovePromotion}
                      className={styles.removePromotionButton}
                    >
                      Retirer
                    </button>
                  </div>
                )}
              </div>

              <div className={styles.formSection}>
                <h2>Informations de livraison</h2>

                <div className={styles.formGroup}>
                  <label htmlFor="shipping_address">Adresse de livraison *</label>
                  <textarea
                    id="shipping_address"
                    name="shipping_address"
                    value={formData.shipping_address}
                    onChange={handleInputChange}
                    required
                    rows="3"
                    placeholder="Indiquez votre adresse complète avec des points de repère..."
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="delivery_phone">Numéro de téléphone de contact *</label>
                  <input
                    type="tel"
                    id="delivery_phone"
                    name="delivery_phone"
                    value={formData.delivery_phone}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: +225 07 12 34 56 78"
                    pattern="[+]?[0-9\s\-\(\)]+"
                  />
                  <small className={styles.helpText}>
                    Ce numéro sera utilisé par le livreur pour vous contacter lors de la livraison
                  </small>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="delivery_city">Ville *</label>
                    <input
                      type="text"
                      id="delivery_city"
                      name="delivery_city"
                      value={formData.delivery_city}
                      onChange={handleInputChange}
                      required
                      placeholder="Ex: Abidjan"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="delivery_region">Région *</label>
                    <input
                      type="text"
                      id="delivery_region"
                      name="delivery_region"
                      value={formData.delivery_region}
                      onChange={handleInputChange}
                      required
                      placeholder="Ex: Lagunes"
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="notes">Notes pour le livreur (optionnel)</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder="Instructions spéciales pour la livraison..."
                  />
                </div>
              </div>

              <div className={styles.formSection}>
                <h2>Méthode de paiement</h2>

                <div className={styles.paymentMethods}>
                  <label className={styles.paymentOption}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="cash_on_delivery"
                      checked={formData.payment_method === 'cash_on_delivery'}
                      onChange={handleInputChange}
                    />
                    <span className={styles.paymentLabel}>
                      <strong>Paiement en espèces à la livraison</strong>
                      <small>Payez en espèces quand vous recevez votre commande</small>
                    </span>
                  </label>

                  <label className={styles.paymentOption}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="mobile_money"
                      checked={formData.payment_method === 'mobile_money'}
                      onChange={handleInputChange}
                    />
                    <span className={styles.paymentLabel}>
                      <strong>Mobile Money</strong>
                      <small>Orange Money, MTN Money, Moov Money</small>
                    </span>
                  </label>

                  <label className={styles.paymentOption}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="bank_transfer"
                      checked={formData.payment_method === 'bank_transfer'}
                      onChange={handleInputChange}
                    />
                    <span className={styles.paymentLabel}>
                      <strong>Virement bancaire</strong>
                      <small>Transfert vers notre compte bancaire</small>
                    </span>
                  </label>
                </div>
              </div>

              {error && (
                <div className={styles.errorMessage}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={styles.submitButton}
              >
                {isSubmitting ? 'Traitement...' : `Confirmer la commande (${parseFloat(total || 0).toLocaleString('fr-FR')} FCFA)`}
              </button>
            </form>
          </div>

          <div className={styles.orderSummary}>
            <h2>Résumé de la commande</h2>

            <div className={styles.summaryItems}>
              {items.map((item) => {
                const itemPrice = parseFloat(item.price || item.unit_price || 0);
                const itemTotal = itemPrice * item.quantity;
                return (
                  <div key={item.id} className={styles.summaryItem}>
                    <div className={styles.itemInfo}>
                      <h4>{item.product_name || item.product?.name}</h4>
                      <p>{item.quantity} × {itemPrice.toLocaleString('fr-FR')} FCFA</p>
                    </div>
                    <div className={styles.itemTotal}>
                      {itemTotal.toLocaleString('fr-FR')} FCFA
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={styles.summaryTotals}>
              <div className={styles.summaryRow}>
                <span>Sous-total</span>
                <span>{parseFloat(calculatedSubtotal || 0).toLocaleString('fr-FR')} FCFA</span>
              </div>

              <div className={styles.summaryRow}>
                <span>Frais de livraison</span>
                <span>{parseFloat(shippingCost || 0).toLocaleString('fr-FR')} FCFA</span>
              </div>

              {settings.tax_enabled && taxAmount > 0 && (
                <div className={styles.summaryRow}>
                  <span>TVA ({(settings.tax_rate * 100).toFixed(0)}%)</span>
                  <span>{parseFloat(taxAmount || 0).toLocaleString('fr-FR')} FCFA</span>
                </div>
              )}

              {promotion && discountAmount > 0 && (
                <div className={styles.summaryRow} style={{ color: '#4CAF50' }}>
                  <span>Remise ({promotion.code})</span>
                  <span>-{parseFloat(discountAmount || 0).toLocaleString('fr-FR')} FCFA</span>
                </div>
              )}

              <div className={styles.summaryTotal}>
                <span>Total</span>
                <span>{parseFloat(total || 0).toLocaleString('fr-FR')} FCFA</span>
              </div>
            </div>

            {formData.payment_method === 'cash_on_delivery' && (
              <div className={styles.cashOnDeliveryInfo}>
                <h3>💵 Paiement à la livraison</h3>
                <p>Préparez le montant exact en espèces pour le livreur.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;











